import React from 'react';
import { Body, Flex, Heading, Input } from '@cognite/cogs.js';
import styled from 'styled-components/macro';
import { Table } from 'antd';
import type { TableProps } from 'antd/es/table';
import { formatDateRecentTime } from 'utils/misc';
import { ConsistencyRuleRunResult } from '../consistencyReportTypes';
import { ConsistencyReportDetail } from '../hooks/useConsistencyReport';
import { calculateSuccessRate } from '../helpers';
import { useConsistencyReportView } from './useConsistencyReportView';

type ConsistencyReportViewProps = {
  report: ConsistencyReportDetail;
};

export const ConsistencyReportView = ({
  report,
}: ConsistencyReportViewProps) => {
  const {
    search,
    setSearch,
    filteredRules,
    failedRuleCount,
    totalFailed,
    goToRule,
    columns,
  } = useConsistencyReportView(report);

  return (
    <Container direction="column" gap={16}>
      <StatsRow direction="row" gap={24} alignItems="center">
        <StatBlock>
          <StatLabel size="x-small">Total Valid</StatLabel>
          <StatValue>{report.totalValid}</StatValue>
        </StatBlock>
        <StatBlock>
          <StatLabel size="x-small">Total Failed</StatLabel>
          <StatValue failed>{report.totalFailed}</StatValue>
        </StatBlock>
        <StatBlock>
          <StatLabel size="x-small">Success rate</StatLabel>
          <StatValue>
            {calculateSuccessRate(report.totalValid, report.totalFailed)}%
          </StatValue>
        </StatBlock>
        <StatBlock>
          <StatLabel size="x-small">Run at</StatLabel>
          <StatValue>{formatDateRecentTime(report.runAt)}</StatValue>
        </StatBlock>
      </StatsRow>

      <Heading level={5}>Rules</Heading>
      <Flex direction="row" justifyContent="space-between" alignItems="center">
        <Body size="small">
          <b>{failedRuleCount}</b> failed rules, <b>{totalFailed}</b> violating
          items across <b>{filteredRules.length}</b> rules
        </Body>
        <SearchInput
          placeholder="Search rules..."
          icon="Search"
          iconPlacement="left"
          value={search}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSearch(e.currentTarget.value)
          }
        />
      </Flex>

      <StyledTable<ConsistencyRuleRunResult>
        rowKey="ruleId"
        columns={columns}
        dataSource={filteredRules}
        onRow={(record: ConsistencyRuleRunResult) => ({
          onClick: () => goToRule(record),
        })}
        pagination={{ defaultPageSize: 20, hideOnSinglePage: true }}
      />
    </Container>
  );
};

const Container = styled(Flex)`
  flex: 1;
  overflow: auto;
  padding: 1.5rem 2rem;
`;

const StatsRow = styled(Flex)`
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--cogs-bg-control--disabled);
`;

const StatBlock = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatLabel = styled(Body)`
  color: var(--cogs-greyscale-grey7);
`;

const StatValue = styled.div<{ failed?: boolean }>`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${({ failed }) => (failed ? 'var(--cogs-red)' : 'inherit')};
`;

const SearchInput = styled(Input)`
  max-width: 260px;
`;

const StyledTableWrapper = styled.div`
  .ant-table {
    background: transparent;
  }
  table {
    border-spacing: 0 4px;
  }
  .ant-table-thead > tr > th,
  .ant-table-tbody > tr > td {
    padding: 10px 16px;
    background: transparent;
  }
  .ant-table-thead > tr > th {
    border-bottom: none;
  }
  .ant-table-row {
    background: var(--cogs-white);
    box-shadow: var(--cogs-z-4);
    cursor: pointer;
  }
`;

function StyledTable<T extends object>(props: TableProps<T>) {
  return (
    <StyledTableWrapper>
      <Table {...props} />
    </StyledTableWrapper>
  );
}
